import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostBinding, computed, input } from '@angular/core';
import { MatProgressBarModule } from '@angular/material/progress-bar';

export type XpPasswordStrengthLevel = 'empty' | 'weak' | 'medium' | 'strong';

export interface XpPasswordStrengthLabels {
	empty: string;
	weak: string;
	medium: string;
	strong: string;
}

const LEVEL_BY_SCORE: XpPasswordStrengthLevel[] = ['empty', 'weak', 'weak', 'medium', 'strong'];
const PROGRESS_BY_LEVEL: Record<XpPasswordStrengthLevel, number> = { empty: 0, weak: 33, medium: 66, strong: 100 };

function passwordRulesMet(password: string, minLength: number): boolean[] {
	return [
		/[a-z]/.test(password),
		/[A-Z]/.test(password),
		/[0-9]/.test(password),
		password.length >= minLength
	];
}

export function xpPasswordStrength(password: string, minLength: number): XpPasswordStrengthLevel {
	return LEVEL_BY_SCORE[passwordRulesMet(password, minLength).filter(Boolean).length];
}

@Component({
	selector: 'xp-password-strength',
	standalone: true,
	imports: [CommonModule, MatProgressBarModule],
	templateUrl: './xp-password-strength.component.html',
	styleUrls: ['./xp-password-strength.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class XpPasswordStrengthComponent {
	password = input('');
	minLength = input(8);
	header = input('');
	ruleLabels = input<string[]>([]);
	scoreLabels = input<XpPasswordStrengthLabels>({ empty: '', weak: '', medium: '', strong: '' });

	rulesMet = computed(() => passwordRulesMet(this.password(), this.minLength()));
	level = computed(() => xpPasswordStrength(this.password(), this.minLength()));
	progress = computed(() => PROGRESS_BY_LEVEL[this.level()]);
	scoreLabel = computed(() => this.scoreLabels()[this.level()]);

	rules = computed(() => this.ruleLabels().map((label, index) => ({ label, met: this.rulesMet()[index] })));

	@HostBinding('attr.data-ui-level')
	get levelAttribute(): XpPasswordStrengthLevel {
		return this.level();
	}
}
